import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, RotateCcw, ArrowRight, Target, Activity, AlertCircle, Layers } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function Flashcards() {
  const { t, lang } = useLang();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("alla");
  const [count, setCount] = useState("10");
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState({});  // id -> "known" | "review"
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [lang]);

  const start = async () => {
    setLoading(true);
    try {
      const params = region !== "alla" ? { region } : {};
      const r = await api.get("/tests", { params });
      const filtered = r.data.filter((tst) => tst.purpose && tst.positive);
      const sample = [...filtered].sort(() => Math.random() - 0.5).slice(0, parseInt(count));
      if (sample.length === 0) {
        toast.warning(t("flash.noneAvail"));
        return;
      }
      setCards(sample); setIdx(0); setFlipped(false); setResults({}); setDone(false);
    } catch (e) {
      toast.error(t("flash.errLoad"));
    } finally {
      setLoading(false);
    }
  };

  const mark = (status) => {
    if (!cards[idx]) return;
    const newRes = { ...results, [cards[idx].id]: status };
    setResults(newRes);
    if (idx + 1 >= cards.length) {
      setDone(true);
    } else {
      setIdx(idx + 1);
      setFlipped(false);
    }
  };

  const stats = useMemo(() => {
    const known = Object.values(results).filter((r) => r === "known").length;
    const review = Object.values(results).filter((r) => r === "review").length;
    return { known, review, total: cards.length };
  }, [results, cards]);

  // ---- SETUP ----
  if (cards.length === 0) {
    return (
      <div data-testid="flash-setup" className="max-w-xl">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
          <span className="title-rule" />{t("flash.eyebrow")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight mb-3">
          {t("flash.title")}
        </h1>
        <p className="text-muted-foreground mb-8">
          {t("flash.lead")}
        </p>

        <Card className="p-6 space-y-5 bg-card">
          <div>
            <label className="text-sm font-medium mb-2 block">{t("flash.region")}</label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="flash-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alla">{t("flash.allRegions")}</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">{t("flash.count")}</label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger data-testid="flash-count"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "15", "20", "30"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={start} disabled={loading} className="w-full" data-testid="flash-start-btn">
            <Layers className="size-4 mr-2" />
            {loading ? t("common.preparing") : t("flash.start")}
          </Button>
        </Card>
      </div>
    );
  }

  // ---- DONE ----
  if (done) {
    const pct = Math.round((stats.known / stats.total) * 100);
    const reviewIds = Object.entries(results).filter(([, s]) => s === "review").map(([id]) => id);
    const reviewCards = cards.filter((c) => reviewIds.includes(c.id));

    return (
      <div data-testid="flash-done" className="max-w-2xl">
        <div className="text-center py-6 rise">
          <div className="font-serif text-7xl font-medium text-primary mb-2">{pct}%</div>
          <p className="text-base text-muted-foreground">
            {t("flash.summary", { known: stats.known, review: stats.review, total: stats.total })}
          </p>
        </div>

        {reviewCards.length > 0 && (
          <div className="mt-8">
            <h2 className="font-serif text-xl font-medium mb-3">
              <span className="title-rule" />{t("flash.reviewThese")}
            </h2>
            <div className="space-y-2">
              {reviewCards.map((c) => (
                <Card key={c.id} className="p-4 bg-card">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.purpose}</div>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-3 justify-center">
          <Button onClick={() => { setCards([]); }} data-testid="flash-restart-btn">
            <RotateCcw className="size-4 mr-2" /> {t("flash.newRound")}
          </Button>
          {reviewCards.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => {
                setCards(reviewCards);
                setIdx(0); setFlipped(false); setResults({}); setDone(false);
              }}
              data-testid="flash-redo-review-btn"
            >
              {t("flash.redoReview", { n: reviewCards.length })} <ArrowRight className="size-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ---- ACTIVE ----
  const c = cards[idx];
  return (
    <div data-testid="flash-active" className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <Badge variant="secondary" className="font-mono">{t("flash.cardOf")} {idx + 1} / {cards.length}</Badge>
        <div className="text-xs text-muted-foreground font-mono">
          {t("flash.knewCount", { n: stats.known })} · {t("flash.reviewCount", { n: stats.review })}
        </div>
      </div>

      <div
        className="relative w-full min-h-[340px] cursor-pointer perspective-1000"
        onClick={() => setFlipped(!flipped)}
        data-testid="flash-card"
      >
        <div
          className={`relative w-full min-h-[340px] transition-transform duration-500`}
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "none",
          }}
        >
          {/* FRONT */}
          <Card
            className="absolute inset-0 p-8 flex flex-col items-center justify-center text-center bg-card border-primary/20"
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
              {c.region} · {c.position}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight leading-tight" data-testid="flash-test-name">
              {c.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-5 max-w-md">
              {c.purpose}
            </p>
            <p className="text-[11px] font-mono uppercase tracking-widest text-accent mt-8">
              {t("flash.tapToReveal")}
            </p>
          </Card>

          {/* BACK */}
          <Card
            className="absolute inset-0 p-6 bg-card border-accent/30 overflow-y-auto"
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">
              {t("flash.answer")} – {c.name}
            </div>
            <div className="space-y-3 text-sm">
              <Row icon={Target} label={t("test.purpose")} text={c.purpose} />
              <Row icon={Activity} label={t("test.execution")} text={c.execution} />
              <Row icon={AlertCircle} label={t("test.positive")} text={c.positive} accent />
            </div>
          </Card>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          onClick={() => mark("review")}
          className="border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          data-testid="flash-review-btn"
        >
          <XCircle className="size-4 mr-2" /> {t("flash.needPractice")}
        </Button>
        <Button onClick={() => mark("known")} data-testid="flash-known-btn">
          <CheckCircle2 className="size-4 mr-2" /> {t("flash.knew")}
        </Button>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, text, accent }) {
  const isNumberedSteps = /^\s*1\.\s.+?(?:\s+2\.\s)/.test(text || "");
  const steps = isNumberedSteps
    ? (text.match(/\d+\.\s+[^]*?(?=\s+\d+\.\s|$)/g) || []).map((s) => s.replace(/^\d+\.\s+/, "").trim())
    : null;

  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className={`size-3 ${accent ? "text-accent" : ""}`} />{label}
      </div>
      {steps ? (
        <ol className={`text-sm leading-relaxed space-y-1 ${accent ? "font-medium" : "text-foreground/85"}`}>
          {steps.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-muted-foreground tabular-nums shrink-0 min-w-[1.25rem]">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className={`text-sm leading-relaxed ${accent ? "font-medium" : "text-foreground/85"}`}>{text}</p>
      )}
    </div>
  );
}
